import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Wallet,
  ShoppingBag,
  Users2,
  TrendingUp,
  Plus,
  Globe,
  Building2,
  ShoppingCart,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { salesService } from '../../services/salesService';
import { ProductForm } from '../../components/ProductForm';

const Home = () => {
  const [openAddProduct, setOpenAddProduct] = React.useState(false);
  const [salesData, setSalesData] = useState<any[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState('');
  const [totalSales, setTotalSales] = useState(0);
  const [numberOfSales, setNumberOfSales] = useState(0);
  const { language, setLanguage } = useLanguage();
  const { user } = useAuth();

  const handleOpenDialog = () => setOpenAddProduct(true);
  const handleCloseDialog = () => setOpenAddProduct(false);
  const handleSuccess = () => {
    // Vous pouvez ajouter ici une logique pour rafraîchir les données si nécessaire
    handleCloseDialog();
  };

  const text = {
    fr: {
      dashboard: 'Tableau de bord',
      welcome: 'Bienvenue sur votre espace de gestion',
      companyName: 'Entreprise',
      totalSales: 'Total des Ventes',
      numberOfSales: 'Nombre de Ventes',
      activeClients: 'Clients Actifs',
      growth: 'Croissance',
      recentSales: 'Dernières Ventes',
      salesChart: 'Évolution des Ventes',
      date: 'Date',
      client: 'Client',
      amount: 'Montant',
      status: 'Statut',
      topClients: 'Meilleurs Clients',
      lastUpdate: 'Dernière mise à jour',
      addProduct: 'Ajouter un produit',
      completed: 'Complété',
      pending: 'En attente',
      productCode: 'Code du produit',
      productName: 'Nom du produit',
      description: 'Description',
      purchasePrice: 'Prix d\'achat',
      salePrice: 'Prix de vente',
      quantity: 'Quantité',
      cancel: 'Annuler',
      save: 'Enregistrer',
      saving: 'Enregistrement en cours',
      totalPurchases: 'Total des Achats',
      numberOfUsers: 'Utilisateurs',
    },
    en: {
      dashboard: 'Dashboard',
      welcome: 'Welcome to your management space',
      companyName: 'Company',
      totalSales: 'Total Sales',
      numberOfSales: 'Number of Sales',
      activeClients: 'Active Clients',
      growth: 'Growth',
      recentSales: 'Recent Sales',
      salesChart: 'Sales Evolution',
      date: 'Date',
      client: 'Client',
      amount: 'Amount',
      status: 'Status',
      topClients: 'Top Clients',
      lastUpdate: 'Last update',
      addProduct: 'Add product',
      completed: 'Completed',
      pending: 'Pending',
      productCode: 'Product Code',
      productName: 'Product Name',
      description: 'Description',
      purchasePrice: 'Purchase Price',
      salePrice: 'Sale Price',
      quantity: 'Quantity',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving',
      totalPurchases: 'Total Purchases',
      numberOfUsers: 'Users',
    }
  };

  const currentText = text[language];

  // Données d'exemple - À remplacer par les données réelles de votre backend
  const stats = {
    totalVentes: '120,000 FC',
    nombreVentes: '45',
    clientsActifs: '28',
    croissance: '+15%',
  };

  const salesDataExample = [
    { date: '2024-01-15', ventes: 4000 },
    { date: '2024-01-16', ventes: 3000 },
    { date: '2024-01-17', ventes: 5000 },
    { date: '2024-01-18', ventes: 2780 },
    { date: '2024-01-19', ventes: 1890 },
    { date: '2024-01-20', ventes: 2390 },
    { date: '2024-01-21', ventes: 3490 },
  ];

  const recentSales = [
    { id: 1, date: '2024-01-21', client: 'Jean Dupont', montant: '15,000 FC', statut: 'Complété' },
    { id: 2, date: '2024-01-21', client: 'Marie Claire', montant: '8,500 FC', statut: 'En attente' },
    { id: 3, date: '2024-01-20', client: 'Pierre Martin', montant: '12,000 FC', statut: 'Complété' },
    { id: 4, date: '2024-01-20', client: 'Sophie Laurent', montant: '9,000 FC', statut: 'Complété' },
  ];

  useEffect(() => {
    const fetchSales = async () => {
      if (user?.companyId) {
        setSalesLoading(true);
        try {
          console.log('Fetching sales for company:', user?.companyId);
          const data = await salesService.getSales(user?.companyId);
          console.log('Sales data received:', data);
          setSalesData(data);
          
          // Calculer le total des ventes
          const total = data.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);
          setTotalSales(total);
          
          // Définir le nombre total de ventes
          setNumberOfSales(data.length);
        } catch (error) {
          console.error('Erreur lors de la récupération des ventes:', error);
          setSalesError('Erreur lors de la récupération des ventes');
        } finally {
          setSalesLoading(false);
        }
      } else {
        console.log('No company ID found in user object:', user);
      }
    };

    fetchSales();
  }, [user?.companyId]);

  return (
    <>
      {/* Header Section */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0D3C4C 0%, #1976d2 100%)',
          color: 'white',
          pt: { xs: 4, sm: 8 },
          pb: { xs: 4, sm: 6 },
          mb: { xs: 3, sm: 6 },
          px: { xs: 2, sm: 3 },
          borderRadius: { xs: '0 0 16px 16px', sm: '0 0 24px 24px' },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/pattern.svg)',
            backgroundSize: 'cover',
            opacity: 0.1,
            animation: 'pulse 4s ease-in-out infinite',
          },
          '@keyframes pulse': {
            '0%': { opacity: 0.1 },
            '50%': { opacity: 0.15 },
            '100%': { opacity: 0.1 },
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mb: { xs: 3, sm: 4 },
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 48, sm: 64 },
                  height: { xs: 48, sm: 64 },
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                <Building2 size={32} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    animation: 'slideIn 0.5s ease-out',
                    '@keyframes slideIn': {
                      from: {
                        opacity: 0,
                        transform: 'translateY(-20px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  {user?.company?.name || user?.companyId || 'Mon Entreprise'}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    opacity: 0.9,
                    fontWeight: 500,
                    animation: 'fadeIn 0.5s ease-out 0.2s both',
                    '@keyframes fadeIn': {
                      from: { opacity: 0 },
                      to: { opacity: 0.9 },
                    },
                  }}
                >
                  {currentText.welcome}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                width: '100%',
              }}
            >
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: '100%', sm: 120 },
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                  },
                }}
              >
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as 'fr' | 'en')}
                  sx={{
                    color: 'white',
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  }}
                >
                  <MenuItem value="fr">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Globe size={16} /> FR
                    </Box>
                  </MenuItem>
                  <MenuItem value="en">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Globe size={16} /> EN
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<Plus size={20} />}
                onClick={handleOpenDialog}
                fullWidth={true}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  borderRadius: 2,
                  px: 3,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                {currentText.addProduct}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: { xs: 2, sm: 4 }, 
          mb: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3 }
        }}
      >
        {/* Stats Grid */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Carte Total des Ventes */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: { xs: 1, sm: 2 },
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(13, 60, 76, 0.1)',
                      color: '#0D3C4C',
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      mr: 2,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(15deg)',
                      },
                    }}
                  >
                    <Wallet size={24} />
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                    }}
                  >
                    {text[language].totalSales}
                  </Typography>
                </Box>
                {salesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        background: 'linear-gradient(45deg, #0D3C4C 30%, #2196F3 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {totalSales.toLocaleString()} {user?.company?.signCurrency}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      <TrendingUp size={16} />
                      +15% depuis le mois dernier
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Carte Nombre de Ventes */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: { xs: 1, sm: 2 },
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(103, 58, 183, 0.1)',
                      color: '#673AB7',
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      mr: 2,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(15deg)',
                      },
                    }}
                  >
                    <ShoppingCart size={24} />
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                    }}
                  >
                    {text[language].numberOfSales}
                  </Typography>
                </Box>
                {salesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        background: 'linear-gradient(45deg, #673AB7 30%, #9C27B0 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {numberOfSales}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      <TrendingUp size={16} />
                      +10% depuis le mois dernier
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Carte Nombre d'utilisateurs */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: { xs: 1, sm: 2 },
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(233, 30, 99, 0.1)',
                      color: '#E91E63',
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      mr: 2,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(15deg)',
                      },
                    }}
                  >
                    <Users2 size={24} />
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                    }}
                  >
                    {text[language].numberOfUsers}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    background: 'linear-gradient(45deg, #E91E63 30%, #FF4081 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  2
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 500,
                  }}
                >
                  <TrendingUp size={16} />
                  +2% depuis le mois dernier
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte Croissance */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: { xs: 1, sm: 2 },
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(237, 108, 2, 0.1)',
                      color: '#ED6C02',
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      mr: 2,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'rotate(15deg)',
                      },
                    }}
                  >
                    <TrendingUp size={24} />
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                    }}
                  >
                    {currentText.growth}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    background: 'linear-gradient(45deg, #ED6C02 30%, #F57C00 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {stats.croissance}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 500,
                  }}
                >
                  <TrendingUp size={16} />
                  +15% depuis le mois dernier
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Graphique et Tableau */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 4 } }}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                {text[language].salesChart}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesDataExample}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="ventes" stroke="#8884d8" name="Ventes" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                height: '100%',
                background: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              }}
            >
              <Typography variant="h6" gutterBottom>
                {text[language].recentSales}
              </Typography>
              {salesLoading ? (
                <Typography>Chargement...</Typography>
              ) : salesError ? (
                <Alert severity="error">{salesError}</Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{text[language].date}</TableCell>
                        <TableCell>{text[language].amount}</TableCell>
                        <TableCell>{text[language].status}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesData?.slice(0, 5).map((sale: any) => (
                        <TableRow key={sale._id}>
                          <TableCell>{new Date(sale.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {sale.totalAmount} {user?.company?.signCurrency}
                          </TableCell>
                          <TableCell>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              backgroundColor: sale.status === 'completed' ? '#e6ffe6' : '#fff3e6',
                              color: sale.status === 'completed' ? '#006600' : '#cc7700'
                            }}>
                              {sale.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Modal d'ajout de produit */}
        <Dialog open={openAddProduct} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{text[language].addProduct}</DialogTitle>
          <ProductForm onSuccess={handleSuccess} />
        </Dialog>
      </Container>
    </>
  );
};

export default Home; 